"use client";

import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, type DropdownProps } from "react-day-picker";

import { cn } from "./utils";
import { buttonVariants } from "./button";

const CURRENT_YEAR = new Date().getFullYear();

/**
 * Replaces react-day-picker's native `<select>` month/year dropdown with a
 * custom popover list — native selects can't be given a fixed, scrollable
 * height (the browser controls that), which made the year list balloon to
 * fill the screen. This gives a compact, always-scrollable panel instead.
 */
function CalendarDropdown({ name, value, onChange, children, caption, "aria-label": ariaLabel }: DropdownProps) {
  const [open, setOpen] = React.useState(false);
  const options = React.Children.toArray(children) as React.ReactElement<{
    value: string | number;
    children: React.ReactNode;
  }>[];
  const isMonth = name === "months";

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#e2e8f0] dark:border-white/10 bg-white dark:bg-white/[0.04] text-[13px] font-semibold text-[#0f172a] dark:text-white whitespace-nowrap hover:border-indigo-300 dark:hover:border-indigo-500/50 transition-colors"
        >
          {caption}
          <ChevronDown className="size-3.5 text-[#94a3b8] dark:text-white/40" />
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="center"
          sideOffset={4}
          className={cn(
            "z-[220] max-h-[200px] overflow-y-auto rounded-lg border border-border bg-popover text-popover-foreground shadow-xl p-1 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            isMonth ? "w-[8.5rem]" : "w-[6.5rem]",
          )}
        >
          {options.map((opt) => {
            const optValue = opt.props.value;
            const active = String(optValue) === String(value);
            return (
              <button
                key={String(optValue)}
                type="button"
                onClick={() => {
                  onChange?.({ target: { value: String(optValue) } } as React.ChangeEvent<HTMLSelectElement>);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center rounded-md px-2 py-1.5 text-left text-[13px] transition-colors",
                  active
                    ? "bg-indigo-600 text-white font-semibold"
                    : "text-[#0f172a] dark:text-white/80 hover:bg-indigo-50 dark:hover:bg-indigo-500/10",
                )}
              >
                {opt.props.children}
              </button>
            );
          })}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "dropdown-buttons",
  fromYear = CURRENT_YEAR - 10,
  toYear = CURRENT_YEAR + 10,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      fromYear={fromYear}
      toYear={toYear}
      className={cn(
        "p-4 rounded-xl border border-[#e2e8f0] dark:border-white/[0.08] bg-white dark:bg-[#151521] shadow-sm",
        className,
      )}
      classNames={{
        months: "flex flex-col sm:flex-row gap-2",
        month: "flex flex-col gap-3",
        caption: "flex justify-center pt-1 relative items-center w-full px-9",
        caption_label:
          "pointer-events-none inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#e2e8f0] dark:border-white/10 bg-white dark:bg-white/[0.04] text-[13px] font-semibold text-[#0f172a] dark:text-white whitespace-nowrap",
        caption_dropdowns: "relative z-10 flex min-w-0 items-center justify-center gap-1.5",
        dropdown_month: "relative min-w-0",
        dropdown_year: "relative min-w-0 flex-shrink-0",
        vhidden: "sr-only",
        nav: "flex items-center gap-1",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "size-7 bg-transparent p-0 border-[#e2e8f0] dark:border-white/[0.08] text-[#475569] dark:text-white/60 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-400 transition-colors",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse",
        head_row: "flex",
        head_cell:
          "rounded-md w-9 font-semibold text-[11px] text-[#94a3b8] dark:text-white/40 tracking-wide",
        row: "flex w-full mt-1",
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-indigo-50 dark:[&:has([aria-selected])]:bg-indigo-500/10 [&:has([aria-selected].day-range-end)]:rounded-r-lg",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-lg [&:has(>.day-range-start)]:rounded-l-lg first:[&:has([aria-selected])]:rounded-l-lg last:[&:has([aria-selected])]:rounded-r-lg"
            : "[&:has([aria-selected])]:rounded-lg",
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "size-9 p-0 font-medium text-[13px] rounded-lg text-[#0f172a] dark:text-white/80 hover:bg-indigo-50 hover:text-indigo-600 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-400 aria-selected:opacity-100 transition-colors",
        ),
        day_range_start:
          "day-range-start aria-selected:bg-indigo-600 aria-selected:text-white",
        day_range_end:
          "day-range-end aria-selected:bg-indigo-600 aria-selected:text-white",
        day_selected:
          "bg-indigo-600 text-white hover:bg-indigo-600 hover:text-white focus:bg-indigo-600 focus:text-white shadow-sm shadow-indigo-500/30 font-semibold",
        day_today:
          "bg-indigo-50 text-indigo-600 font-semibold dark:bg-indigo-500/10 dark:text-indigo-400",
        day_outside: "day-outside text-[#cbd5e1] dark:text-white/20 aria-selected:text-white",
        day_disabled: "opacity-40 cursor-not-allowed hover:bg-transparent",
        day_range_middle:
          "aria-selected:bg-indigo-50 aria-selected:text-indigo-600 dark:aria-selected:bg-indigo-500/10 dark:aria-selected:text-indigo-400",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft className={cn("size-4", className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight className={cn("size-4", className)} {...props} />
        ),
        Dropdown: CalendarDropdown,
      }}
      {...props}
    />
  );
}

export { Calendar };
